import { tallyItems, topicBreakdown, type TopicScore } from 'components/result/topicBreakdown';
import type { MapCell } from './primitives';

/** What a result needs from one graded answer; the caller decides what counts as answered. */
export interface ResultAnswerInput {
  questionId: string;
  sectionId: string;
  sectionTitle: string;
  isCorrect: boolean;
  answered: boolean;
  itemsCorrect?: number;
  itemsTotal?: number;
  gradingPending?: boolean;
  /** Seconds spent on the question. */
  timeSpent?: number;
}

export interface SubjectConfigInput {
  subjectName: string;
  pointsPerQuestion: number;
}

export interface CategoryResultData {
  /** Items right / wrong / left blank out of all items asked (a Reading passage counts each sub-question). */
  correct: number;
  wrong: number;
  skipped: number;
  total: number;
  percent: number;
  /** Seconds across all answers; 0 when none was timed. */
  answeredSeconds: number;
  topics: TopicScore[];
  /** One cell per answer, in the order of the review. */
  cells: MapCell[];
}

export function buildCategoryResult(answers: ResultAnswerInput[]): CategoryResultData {
  const { correct, total } = tallyItems(answers);
  const skipped = answers.reduce((sum, a) => (a.answered || a.gradingPending ? sum : sum + (a.itemsTotal ?? 1)), 0);
  const wrong = Math.max(0, total - correct - skipped);
  return {
    correct,
    wrong,
    skipped,
    total,
    percent: total > 0 ? Math.round((correct / total) * 100) : 0,
    answeredSeconds: answers.reduce((sum, a) => sum + (a.timeSpent ?? 0), 0),
    topics: topicBreakdown(answers).filter((t) => t.title.trim() !== ''),
    cells: answers.map((a, i) => ({
      n: i + 1,
      state: !a.answered ? 'skip' : a.isCorrect ? 'ok' : 'no',
    })),
  };
}

/** DTM prices one correct answer of a single subject at 3.1 points (a 30-question subject tops out at 93). */
export const DTM_POINTS_PER_QUESTION = 3.1;

export interface DtmPoints {
  points: number;
  max: number;
}

/**
 * DTM points: correct answers times the subject's price. A full variant uses each subject's own
 * `pointsPerQuestion`; a single subject uses 3.1. Null when a full variant has no prices to count with.
 */
export function dtmPoints(answers: ResultAnswerInput[], subjectConfigs: SubjectConfigInput[] | undefined): DtmPoints | null {
  const configs = subjectConfigs ?? [];
  const full = configs.length >= 3;
  if (full) {
    const priceOf = (title: string) => configs.find((c) => c.subjectName.trim() === title.trim())?.pointsPerQuestion;
    const subjects = new Map<string, ResultAnswerInput[]>();
    answers.forEach((a) => subjects.set(a.sectionTitle, [...(subjects.get(a.sectionTitle) ?? []), a]));
    let points = 0;
    let max = 0;
    for (const [title, rows] of subjects) {
      const price = priceOf(title);
      if (price === undefined) return null;
      const tally = tallyItems(rows);
      points += tally.correct * price;
      max += tally.total * price;
    }
    return { points: round1(points), max: round1(max) };
  }
  const { correct, total } = tallyItems(answers);
  return { points: round1(correct * DTM_POINTS_PER_QUESTION), max: round1(total * DTM_POINTS_PER_QUESTION) };
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * The test's own score, 0 to 100: points earned over points possible, to one decimal. It follows the points the
 * test awards (a question's weight), so it is not an official certificate score. Null when the test has no
 * points to earn, which leaves the share correct to speak for the result.
 */
export function testBall(score: number | null | undefined, maxScore: number | null | undefined): number | null {
  if (typeof score !== 'number' || typeof maxScore !== 'number' || !Number.isFinite(score) || !Number.isFinite(maxScore)) return null;
  if (maxScore <= 0 || score < 0) return null;
  return round1(Math.min(100, (score / maxScore) * 100));
}

/** The Milliy sertifikat ladder and the lowest share that earns each step (mirrors category-scoring). */
export const MILLIY_LADDER: Array<{ label: string; min: number }> = [
  { label: 'C', min: 60 },
  { label: 'C+', min: 65 },
  { label: 'B', min: 70 },
  { label: 'B+', min: 75 },
  { label: 'A', min: 80 },
  { label: 'A+', min: 86 },
];

export const CEFR_LADDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;

/** Where a CEFR step stands against the learner: behind them, theirs, their target, or not yet. */
export function cefrStepState(step: string, level: string | null, target?: string | null): 'passed' | 'here' | 'target' | 'ahead' {
  const at = (CEFR_LADDER as readonly string[]).indexOf(step);
  const here = level ? (CEFR_LADDER as readonly string[]).indexOf(level) : -1;
  if (here >= 0 && at < here) return 'passed';
  if (at === here) return 'here';
  if (target === step) return 'target';
  return 'ahead';
}

/** Seconds into whole minutes, never under one once any time was spent. */
export const toMinutes = (seconds: number): number => (seconds <= 0 ? 0 : Math.max(1, Math.round(seconds / 60)));
